import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiChevronDown, FiChevronRight, FiGrid, FiList } from 'react-icons/fi';
import centrosCostoService from '../services/centrosCostoService';
import Loading from '../components/Loading';
import './CentrosCostoPage.css';

const ensureArray = (v) => Array.isArray(v) ? v : [];

const CentrosCostoPage = () => {
  const [centros, setCentros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [vista, setVista] = useState('tabla');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());

  const [form, setForm] = useState({
    codigo: '', nombre: '', tipo: 'departamento', responsable: '', padre: '', presupuesto_anual: 0, activo: true,
  });

  useEffect(() => { cargarCentros(); }, []);

  const cargarCentros = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filtroTipo) filters.tipo = filtroTipo;
      const res = await centrosCostoService.getAll(filters);
      setCentros(ensureArray(res.data.results || res.data));
    } catch { toast.error('Error al cargar centros de costo'); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargarCentros(); }, [filtroTipo]);

  const handleSave = async () => {
    try {
      const data = { ...form };
      if (!data.padre) delete data.padre;
      if (!data.responsable) delete data.responsable;
      if (editing) {
        await centrosCostoService.update(editing.id, data);
        toast.success('Centro actualizado');
      } else {
        await centrosCostoService.create(data);
        toast.success('Centro creado');
      }
      setModalOpen(false);
      setEditing(null);
      cargarCentros();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este centro de costo?')) return;
    try { await centrosCostoService.delete(id); toast.success('Eliminado'); cargarCentros(); }
    catch { toast.error('Error al eliminar'); }
  };

  const handleEdit = (c) => {
    setEditing(c);
    setForm({ codigo: c.codigo, nombre: c.nombre, tipo: c.tipo, responsable: c.responsable || '', padre: c.padre || '', presupuesto_anual: c.presupuesto_anual || 0, activo: c.activo });
    setModalOpen(true);
  };

  const resetForm = () => setForm({ codigo: '', nombre: '', tipo: 'departamento', responsable: '', padre: '', presupuesto_anual: 0, activo: true });

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);

  const getTipoBadge = (tipo) => {
    const map = { departamento: 'badge-blue', sucursal: 'badge-purple', proyecto: 'badge-green' };
    return map[tipo] || 'badge-gray';
  };

  const rootCentros = centros.filter(c => !c.padre);
  const getChildren = (parentId) => centros.filter(c => c.padre === parentId);

  const renderTreeNode = (centro, level = 0) => (
    <React.Fragment key={centro.id}>
      <tr>
        <td style={{ paddingLeft: `${level * 24 + 16}px` }}>
          {getChildren(centro.id).length > 0 ? (
            <button className="btn-icon-sm" onClick={() => toggleExpand(centro.id)}>
              {expandedIds.has(centro.id) ? <FiChevronDown /> : <FiChevronRight />}
            </button>
          ) : <span style={{ width: 24, display: 'inline-block' }}></span>}
          <span className="mono">{centro.codigo}</span>
        </td>
        <td><strong>{centro.nombre}</strong></td>
        <td><span className={`badge ${getTipoBadge(centro.tipo)}`}>{centro.tipo}</span></td>
        <td>{centro.responsable_nombre || centro.responsable || '-'}</td>
        <td className="mono right">{formatCurrency(centro.presupuesto_anual)}</td>
        <td><span className={`badge ${centro.activo ? 'badge-green' : 'badge-gray'}`}>{centro.activo ? 'Activo' : 'Inactivo'}</span></td>
        <td>
          <div className="acciones-cell">
            <button className="btn-icon" onClick={() => handleEdit(centro)}><FiEdit2 /></button>
            <button className="btn-icon btn-danger" onClick={() => handleDelete(centro.id)}><FiTrash2 /></button>
          </div>
        </td>
      </tr>
      {expandedIds.has(centro.id) && getChildren(centro.id).map(child => renderTreeNode(child, level + 1))}
    </React.Fragment>
  );

  if (loading) return <Loading />;

  return (
    <div className="centros-costo-page">
      <div className="page-header">
        <h1>Centros de Costo</h1>
        <div style={{ display: 'flex', gap: 12 }}>
          <div className="view-toggle">
            <button className={vista === 'tabla' ? 'active' : ''} onClick={() => setVista('tabla')}><FiList /></button>
            <button className={vista === 'arbol' ? 'active' : ''} onClick={() => setVista('arbol')}><FiGrid /></button>
          </div>
          <button className="btn-primary" onClick={() => { resetForm(); setEditing(null); setModalOpen(true); }}><FiPlus /> Nuevo Centro</button>
        </div>
      </div>

      <div className="filtros-container">
        <select className="select-filtro" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
          <option value="">Todos los tipos</option>
          <option value="departamento">Departamento</option>
          <option value="sucursal">Sucursal</option>
          <option value="proyecto">Proyecto</option>
        </select>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr><th>Codigo</th><th>Nombre</th><th>Tipo</th><th>Responsable</th><th style={{ textAlign: 'right' }}>Presupuesto Anual</th><th>Estado</th><th style={{ textAlign: 'right' }}>Acciones</th></tr>
          </thead>
          <tbody>
            {centros.length === 0 ? (
              <tr><td colSpan="7" style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Sin centros de costo</td></tr>
            ) : vista === 'arbol' ? (
              rootCentros.map(c => renderTreeNode(c))
            ) : (
              centros.map(c => (
                <tr key={c.id}>
                  <td className="mono">{c.codigo}</td>
                  <td><strong>{c.nombre}</strong></td>
                  <td><span className={`badge ${getTipoBadge(c.tipo)}`}>{c.tipo}</span></td>
                  <td>{c.responsable_nombre || c.responsable || '-'}</td>
                  <td className="mono right">{formatCurrency(c.presupuesto_anual)}</td>
                  <td><span className={`badge ${c.activo ? 'badge-green' : 'badge-gray'}`}>{c.activo ? 'Activo' : 'Inactivo'}</span></td>
                  <td><div className="acciones-cell"><button className="btn-icon" onClick={() => handleEdit(c)}><FiEdit2 /></button><button className="btn-icon btn-danger" onClick={() => handleDelete(c.id)}><FiTrash2 /></button></div></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{editing ? 'Editar Centro de Costo' : 'Nuevo Centro de Costo'}</h2>
            <div className="form-grid">
              <div className="form-group"><label>Codigo *</label><input value={form.codigo} onChange={e => setForm({ ...form, codigo: e.target.value })} placeholder="CC-001" /></div>
              <div className="form-group"><label>Nombre *</label><input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="Nombre del centro" /></div>
              <div className="form-group"><label>Tipo</label>
                <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}>
                  <option value="departamento">Departamento</option><option value="sucursal">Sucursal</option><option value="proyecto">Proyecto</option>
                </select>
              </div>
              <div className="form-group"><label>Presupuesto Anual</label><input type="number" value={form.presupuesto_anual} onChange={e => setForm({ ...form, presupuesto_anual: e.target.value })} /></div>
              <div className="form-group form-check" style={{ gridColumn: 'span 2' }}>
                <label><input type="checkbox" checked={form.activo} onChange={e => setForm({ ...form, activo: e.target.checked })} /> Activo</label>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleSave}>{editing ? 'Actualizar' : 'Crear'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CentrosCostoPage;
